import { orderBy, sortBy } from "lodash";
import { isSameDay, parseISO } from "date-fns";
import { ConsultantPipelineCardFragment } from "@bonliva-crm/graphql";
import { ConsultantPipelineStatus } from "@bonliva-crm/shared";
import {
  BoardDescription,
  CardDescription,
  ColumnDescription
} from "../kanban-board/kanban-types";

export function applyCardsToBoard(
  cards: ConsultantPipelineCardFragment[],
  state: BoardDescription<ConsultantPipelineCardFragment>
) {
  const newState = { ...state };

  cards = sortBy(cards, (x) => x.order);

  for (const column of newState.columns) {
    column.cards = [];
  }

  const columnsById = newState.columns.reduce((acc, column) => {
    acc[column.id] = column;
    return acc;
  }, {} as Record<string, ColumnDescription<ConsultantPipelineCardFragment>>);

  // Always create a fresh wrapper object so React (and react-beautiful-dnd's
  // memoized renderCard pipeline) detects the prop change and re-renders.
  // Earlier we preserved wrapper identity to avoid a perceived "card
  // disappears" flicker, but the real cause of that flicker was the
  // kanbanCards cache going stale (which we now fix at the cache layer via
  // mutation `update` callbacks). Preserving identity here masked a
  // separate bug: after editing a card's data via the menu the wrapper
  // stayed the same reference so memoized children skipped re-rendering,
  // and the new value (colour / "Nästa kontakt" date) only appeared after
  // a hover forced internal state change. The `draggableId` is `card._id`
  // which stays stable across re-creations, so the dnd library tracks the
  // same draggable across renders.
  for (const card of cards) {
    const column = columnsById[card.columnId];
    if (column) {
      column.cards.push({
        id: card._id,
        title: card.title,
        issue: card
      });
    }
  }

  return newState;
}

export function upsertCardInBoard(
  card: ConsultantPipelineCardFragment,
  state: BoardDescription<ConsultantPipelineCardFragment>
): BoardDescription<ConsultantPipelineCardFragment> {
  // Always create a fresh wrapper (same rationale as applyCardsToBoard) so
  // the kanban library / React detects the prop change and re-renders.
  // draggableId === card._id is stable across re-creations.
  const newColumns = state.columns.map((col) => ({
    ...col,
    cards: col.cards.filter((c) => c.id !== card._id)
  }));

  const target = newColumns.find((c) => c.id === card.columnId);
  if (target) {
    const wrapper: CardDescription<ConsultantPipelineCardFragment> = {
      id: card._id,
      title: card.title,
      issue: card
    };
    target.cards.push(wrapper);
    // Break ties in favour of the moved card. Background: a `moveCard`
    // mutation persists the moved card at `beforeCardId.order` and shifts
    // every other card in the column with `order >= newOrder` by +1. The
    // subscription patch fetches only the *moved* card by id, so OTHER
    // cards in the column still carry their pre-shift (stale) order in the
    // Apollo cache. Without the tie-break, a plain `sortBy(order)` is
    // stable: cards already in the column appear before the newly-pushed
    // one when their cached order ties with `card.order`, and the moved
    // card visually drops one (or more) positions. The backend's intent —
    // always — was to place the moved card strictly before any other
    // card at that order, so we encode that intent in the sort.
    // See CRMDEV-6787 follow-up (Ranjh-under-Pia → drifts under Nikolaos).
    target.cards = orderBy(
      target.cards,
      [(c) => c.issue.order, (c) => (c.id === card._id ? 0 : 1)],
      ["asc", "asc"]
    );
  }

  return { ...state, columns: newColumns };
}

export function removeCardFromBoard(
  cardId: string,
  state: BoardDescription<ConsultantPipelineCardFragment>
): BoardDescription<ConsultantPipelineCardFragment> {
  return {
    ...state,
    columns: state.columns.map((col) => ({
      ...col,
      cards: col.cards.filter((c) => c.id !== cardId)
    }))
  };
}

export function sortBoard(state: BoardDescription<ConsultantPipelineCardFragment>) {
  const newState = { ...state };

  for (const col of newState.columns) {
    if (col.title === ConsultantPipelineStatus.RelevantInTheFuture) {
      col.cards = orderBy(
        col.cards,
        [
          (x) =>
            isSameDay(
              parseISO(x.issue.consultant.nextContactDate?.date as any as string),
              new Date()
            ),
          (x) => x.issue.consultant.nextContactDate?.date
        ],
        ["desc", "asc"]
      );
    }
  }

  return newState;
}
